import { useMemo } from "react";
import { useSearchParams } from "react-router";

import {
  EmptystatePuzzleColored,
  EmptystateSearchNoResultsColored,
} from "components/icons/generated";
import SearchInput from "components/SearchInput";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import { URL_SEARCH_KEY } from "constants/urlQueryKeys";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import { useInitialSort } from "ds/components/Table/hooks/useInitialSort";
import TableSimple from "ds/components/TableSimple";
import useAnalytics from "hooks/useAnalytics";
import { fuzzySearch } from "utils/fuzzySearch";

import { FILTERS_ORDER_SETTINGS_KEY_VERSION_RESOURCES, NO_STICKY_RIGHT } from "./constants";
import { MODULE_VERSION_RESOURCES_TABLE_CELLS } from "./Table/Cells";
import {
  INITIAL_SORT_DIRECTION,
  INITIAL_SORT_OPTION,
  MODULE_VERSION_RESOURCES_TABLE_FILTERS_STORAGE_KEY,
  SORT_CONFIG,
} from "./Table/constants";
import { useSortingModuleVersionResources } from "./Table/useSortingModuleVersionResources";
import useModuleVersionResourcesTableStore from "./Table/useTableStore";
import ModuleVersionPageIntoTitle from "../components/PageIntoTitle";
import ModuleVersionScrollableWrapper from "../components/ScrollableWrapper";

type ModuleVersionResourcesProps = {
  resources: Array<{
    name: string;
    type: string;
  }>;
};

const ModuleVersionResources = ({ resources }: ModuleVersionResourcesProps) => {
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get(URL_SEARCH_KEY);
  const trackAnalytics = useAnalytics();

  useInitialSort(INITIAL_SORT_OPTION, INITIAL_SORT_DIRECTION);

  const filteredResources = useMemo(() => {
    const rows = resources.map((item) => ({
      id: `${item.type}.${item.name}`,
    }));

    if (searchQuery) {
      return fuzzySearch(rows, searchQuery.trim(), {
        keys: ["id"],
        scoreThreshold: -1000,
      });
    }

    return rows;
  }, [resources, searchQuery]);

  const sortedResources = useSortingModuleVersionResources(filteredResources);

  return (
    <>
      <PageInfoHeaderWrapper>
        <ModuleVersionPageIntoTitle title="Resources" tooltip="Resources the module creates." />
        <PageInfoHeaderActions>
          <SearchInput
            placeholder={SEARCH_PLACEHOLDERS.MODULE_RESOURCES}
            filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY_VERSION_RESOURCES}
          />
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>
      <ModuleVersionScrollableWrapper padding="0">
        {sortedResources.length === 0 && (
          <EmptyState
            padding="x-large"
            icon={searchQuery ? EmptystateSearchNoResultsColored : EmptystatePuzzleColored}
            caption={
              searchQuery ? "No resources match your search query" : "This module has no resources"
            }
            fullHeight
            announce={!!searchQuery}
          />
        )}
        {sortedResources.length > 0 && (
          <TableSimple
            data={sortedResources}
            store={useModuleVersionResourcesTableStore}
            ariaLabel="Resources"
            trackAnalytics={trackAnalytics}
            cellPerColumn={MODULE_VERSION_RESOURCES_TABLE_CELLS}
            filtersStorageKey={MODULE_VERSION_RESOURCES_TABLE_FILTERS_STORAGE_KEY}
            sortConfig={SORT_CONFIG}
            hasPagination={false}
            hideSettings
            stickyRight={NO_STICKY_RIGHT}
          />
        )}
      </ModuleVersionScrollableWrapper>
    </>
  );
};

export default ModuleVersionResources;
